import React, { useCallback, useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { isManager } from "@/lib/access";
import { F, LOC_BLANK as BLANK, LOC_FIELDS as FIELDS } from "@/components/vault/common";

export default function VaultLocations() {
  const { user } = useAuth();
  const mgr = isManager(user);
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/vault/locations").then((r) => setRows(r.data)).catch((e) => toast.error(formatErr(e))), []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setBusy(true);
    const body = Object.fromEntries(Object.entries(edit).filter(([k]) => k in BLANK).map(([k, v]) => [k, v === "" ? null : v]));
    try { await (edit.id ? api.patch(`/vault/locations/${edit.id}`, body) : api.post("/vault/locations", body)); toast.success("Location saved"); setEdit(null); load(); } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="vault-locations-page" className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500">File Vault</div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Storage locations</h1>
          <p className="text-sm text-slate-500 mt-1">Premises › room › rack / cabinet › shelf › section. Files are placed at one of these.</p>
        </div>
        {mgr && <Button data-testid="vault-loc-add" onClick={() => setEdit(BLANK)} className="bg-blue-700 hover:bg-blue-800 text-white gap-2"><Plus className="w-4 h-4" />Add location</Button>}
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50"><tr>{["Premises", "Room", "Storage unit", "Shelf", "Section", "Files", "Status", ""].map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((l) => (
              <tr key={l.id} className={`border-t border-slate-100 ${l.is_active ? "" : "text-slate-400"}`} data-testid={`vault-loc-${l.id}`}>
                <td className="px-3 py-2">{l.premises}</td><td className="px-3 py-2">{l.room || "—"}</td><td className="px-3 py-2 font-medium">{l.storage_unit}</td>
                <td className="px-3 py-2">{l.shelf || "—"}</td><td className="px-3 py-2">{l.section || "—"}</td><td className="px-3 py-2 mono">{l.file_count}</td>
                <td className="px-3 py-2 text-xs">{l.is_active ? "Active" : "Inactive"}</td>
                <td className="px-3 py-2 text-right">{mgr && <Button size="sm" variant="ghost" data-testid={`vault-loc-edit-${l.id}`} onClick={() => setEdit({ ...BLANK, ...l })} className="h-7 text-xs">Edit</Button>}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-slate-500">No storage locations yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="vault-loc-form">
          <DialogHeader><DialogTitle>{edit?.id ? "Edit location" : "Add storage location"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {edit && FIELDS.map(([k, l, ph]) => <F key={k} label={l} className={k === "remarks" || k === "premises" ? "col-span-2" : ""}><Input data-testid={`vl-${k}`} value={edit[k] || ""} placeholder={ph} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} /></F>)}
            {edit?.id && <label className="col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" data-testid="vl-active" className="accent-blue-700" checked={edit.is_active} onChange={(e) => setEdit({ ...edit, is_active: e.target.checked })} />Active (inactive locations can't receive files)</label>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button>
            <Button data-testid="vl-save" disabled={busy || !edit?.premises || !edit?.storage_unit} onClick={save} className="bg-blue-700 hover:bg-blue-800 text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
